import React, {useState, useEffect, useImperativeHandle, forwardRef, useCallback, useRef} from 'react';
import DataTable from 'react-data-table-component';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import ReactLoading from "react-loading";
import "./TableBase.css";
import { canOpenTableRow } from '../../helpers/TableRowNavigation';

const sortIcon = <ArrowDownward />;
const premiumTableStyles = {
    table: {
        style: {
            backgroundColor: 'transparent',
        },
    },
    responsiveWrapper: {
        style: {
            overflowX: 'auto',
        },
    },
    headRow: {
        style: {
            minHeight: '46px',
            borderBottom: '1px solid #e2ebe6',
            backgroundColor: '#f5f9f7',
        },
    },
    headCells: {
        style: {
            paddingLeft: '16px',
            paddingRight: '16px',
            color: '#71867c',
            fontSize: '10px',
            fontWeight: 900,
            letterSpacing: '.55px',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
        },
    },
    rows: {
        style: {
            minHeight: '66px',
            color: '#4c6359',
            fontSize: '12px',
            fontWeight: 500,
            borderBottomColor: '#edf1f6',
            backgroundColor: '#ffffff',
            transition: 'background-color .16s ease',
            '&:not(:last-of-type)': {
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                borderBottomColor: '#edf1f6',
            },
            '&:hover': {
                backgroundColor: '#f5faf7',
            },
        },
        highlightOnHoverStyle: {
            color: '#244d3e',
            backgroundColor: '#f1f8f4',
            outline: 'none',
            boxShadow: 'inset 3px 0 0 #278969',
        },
    },
    cells: {
        style: {
            paddingLeft: '16px',
            paddingRight: '16px',
            lineHeight: 1.4,
        },
    },
    pagination: {
        style: {
            minHeight: '58px',
            color: '#708098',
            borderTop: '1px solid #e8edf5',
            backgroundColor: '#ffffff',
            fontSize: '11px',
        },
        pageButtonsStyle: {
            width: '32px',
            height: '32px',
            padding: '7px',
            margin: '0 2px',
            borderRadius: '8px',
            fill: '#66758b',
            '&:disabled': {
                fill: '#bec8d4',
            },
            '&:hover:not(:disabled)': {
                backgroundColor: '#edf7f0',
                fill: '#238264',
            },
            '&:focus': {
                outline: 'none',
                backgroundColor: '#edf7f0',
            },
        },
    },
    progress: {
        style: {
            minHeight: '190px',
            backgroundColor: '#ffffff',
        },
    },
    noData: {
        style: {
            minHeight: '190px',
            backgroundColor: '#ffffff',
        },
    },
};

const TableBase = forwardRef((props, ref) => {

    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterText, setFilterText] = useState('');
    const [debouncedFilterText, setDebouncedFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const latestRequestRef = useRef(0);
    const abortRef = useRef(null);
    const extraQueryParamsKey = JSON.stringify(props.extraQueryParams || {});
    const fetchData = useCallback(async (page, size = perPage, search = debouncedFilterText) => {
        const requestId = ++latestRequestRef.current;
        if (abortRef.current) abortRef.current.abort();
        const abort = new AbortController(); abortRef.current = abort;
        setLoading(true); setError('');
        var token = localStorage.getItem('token');

        try {
            const extraParams = JSON.parse(extraQueryParamsKey);
            const query = new URLSearchParams({ page, size, search: search.trim(), ...extraParams }).toString();
            const response = await fetch(`${process.env.REACT_APP_API_URL + props.dataEndpoint}?${query}`, {
                signal: abort.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const result = await response.json();
            if (requestId === latestRequestRef.current && !abort.signal.aborted) {
                setData(Array.isArray(result.data) ? result.data : []);
                setTotalRows(result.total != null ? Number(result.total) || 0 : (result.data || []).length);
            }
        } catch (error) {
            if (requestId === latestRequestRef.current && !abort.signal.aborted) {
                setData([]); setTotalRows(0);
                setError('Kayıtlar yüklenemedi. Bağlantınızı kontrol edip tekrar deneyin.');
            }
        } finally {
            if (requestId === latestRequestRef.current && !abort.signal.aborted) {
                setLoading(false);
            }
        }
    }, [props.dataEndpoint, perPage, debouncedFilterText, extraQueryParamsKey]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const normalizedSearch = filterText.trim();
            if (normalizedSearch !== debouncedFilterText) {
                setCurrentPage(1);
                setResetPaginationToggle(value => !value);
                setDebouncedFilterText(normalizedSearch);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [filterText, debouncedFilterText]);

    useEffect(() => {
        fetchData(currentPage, perPage, debouncedFilterText);
        return () => { if (abortRef.current) abortRef.current.abort(); };
    }, [fetchData, currentPage, perPage, debouncedFilterText]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    useImperativeHandle(ref, () => ({
        fetchData: (page, size, search) => fetchData(page, size, search),
    }));
    const handleRowClick = (row, event) => {
        if (!props.onRowClicked || !canOpenTableRow(event)) return;
        props.onRowClicked(row, event);
    };
    // The grid only forwards events from tagged targets. Custom cells can contain nested text/icons;
    // handle their bubbles explicitly, while keeping links, selects and action buttons independent.
    const columns = props.columns.map(column => !props.onRowClicked || !column.cell || column.button || column.ignoreRowClick ? column : {
        ...column,
        cell: (row, ...args) => <div className="sz-table-cell-content" onClick={event => { handleRowClick(row, event); event.stopPropagation(); }}>{column.cell(row, ...args)}</div>,
    });
    const subHeaderComponent = (
        <div className="sz-table-toolbar">
            <div className="sz-table-search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                    type="search"
                    aria-label="Tabloda ara"
                    placeholder={props.searchPlaceholder || "Kayıtlarda ara..."}
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                />
                {filterText && (
                    <button type="button" onClick={() => setFilterText('')} aria-label="Aramayı temizle" title="Aramayı temizle">
                        <i className="fa fa-times" />
                    </button>
                )}
            </div>
        </div>
    );
    return (
        <div className={`sz-data-table${props.onRowClicked ? ' sz-data-table-clickable' : ''}`}>
            {error && <div className="sz-table-error" role="alert"><span>{error}</span><button type="button" onClick={() => fetchData(currentPage, perPage, debouncedFilterText)}>Tekrar dene</button></div>}

            <DataTable
                columns={columns}
                data={data}
                customStyles={premiumTableStyles}
                pagination
                paginationServer
                paginationDefaultPage={currentPage}
                paginationResetDefaultPage={resetPaginationToggle}
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 25, 50]}
                sortIcon={sortIcon}
                noDataComponent={
                    <div className="sz-table-empty">
                        <span><i className="fa fa-inbox" /></span>
                        <strong>Kayıt bulunamadı</strong>
                        <small>Arama kriterlerinizi değiştirerek tekrar deneyin.</small>
                    </div>
                }
                highlightOnHover
                pointerOnHover={Boolean(props.onRowClicked)}
                onRowClicked={handleRowClick}
                persistTableHead
                responsive
                subHeader
                subHeaderComponent={subHeaderComponent}
                paginationComponentOptions={{
                    rowsPerPageText: 'Sayfa başına satır:',
                    rangeSeparatorText: '/',
                }}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                progressPending={loading}
                progressComponent={
                    <div className="sz-table-loading">
                        <ReactLoading type="spin" width={'26px'} height={'26px'} color="#238264" />
                        <span>Kayıtlar hazırlanıyor...</span>
                    </div>
                }
            />
        </div>
    );
});

export default TableBase;
